import React, { useState, useEffect, useMemo } from "react";
import { Spinner, useBreakpointValue } from "@chakra-ui/react";
import { CustomModal } from "components/RFQ/Modal/CustomModal";
import { Button } from "newui/components/Button";
import type { Clarification } from "../../types/clarification.type";
import dayjs from "newui/utils/dayjs";
import { DateInputWithLabel, TimePickerWithLabel } from "newui/components/ui/InputWithLabel";
import FormikFieldError from "newui/components/ui/FormikFieldError";
import { ClarificationSendList } from "./ClarificationSendList";
import { SupplierAccordionPanel } from "./SupplierAccordionPanel";
import { Modal, ModalSize } from "newui/components/ui/Modal";

interface SendConfirmationModalProps {
  clarifications: Clarification[];
  isLoading: boolean;
  isOpen: boolean;
  /**
   * Optional explicit supplier name. When omitted, the name is derived from
   * the clarifications. Ignored when the clarifications span multiple
   * suppliers (multi-supplier mode renders an accordion instead).
   */
  supplierName?: string;
  onClose: () => void;
  onConfirm: (dateDue: string) => Promise<void>;
}

export const SendConfirmationModal: React.FC<SendConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  clarifications,
  supplierName,
  isLoading,
}) => {
  const [dateDue, setDateDue] = useState(dayjs(Date.now()).add(7, "days").toDate());
  const [timeDue, setTimeDue] = useState("5:00 PM");
  const [timezone, setTimezone] = useState(dayjs.tz.guess());
  const [error, setError] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);

  const uniqueSupplierNames = useMemo(() => {
    const names = new Set<string>();
    clarifications.forEach((c) => {
      if (c.supplier_name) {
        names.add(c.supplier_name);
      }
    });
    return Array.from(names);
  }, [clarifications]);

  const isMultiSupplier = uniqueSupplierNames.length > 1;
  const supplierCount = uniqueSupplierNames.length;
  const derivedSupplierName =
    supplierName || uniqueSupplierNames[0] || "supplier";

  // Responsive modal size — wider on desktop for multi-supplier mode so the
  // two-column layout has room to breathe.
  const modalSize = useBreakpointValue({ base: "lg", md: "3xl" });
  const isDesktopMulti = useBreakpointValue({ base: false, md: true });
  const effectiveSize = (isMultiSupplier ? modalSize ?? "lg" : "lg") as ModalSize;

  useEffect(() => {
    if (isOpen) {
      const defaultDate = dayjs()
        .add(7, "days")
        .toDate();
      setDateDue(defaultDate);
      setTimeDue("5:00 PM");
      setTimezone(dayjs.tz.guess());
      setError("");
      setIsConfirmed(false);
    }
  }, [isOpen]);

  const combineDateAndTime = (): dayjs.Dayjs => {
    const dateStr = dayjs(dateDue).format('YYYY-MM-DD');
    const timeMatch = timeDue.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)?$/i);

    let hour = 17;
    let minute = 0;

    if (timeMatch) {
      hour = parseInt(timeMatch[1], 10);
      minute = parseInt(timeMatch[2], 10);
      const period = timeMatch[3];

      if (period) {
        if (period.toUpperCase() === 'PM' && hour !== 12) {
          hour += 12;
        }
        if (period.toUpperCase() === 'AM' && hour === 12) {
          hour = 0;
        }
      }
    }

    return dayjs.tz(`${dateStr} ${hour}:${minute}`, 'YYYY-MM-DD HH:mm', timezone);
  };

  const validateDateTime = (): boolean => {
    if (!dateDue) {
      setError("Response due date is required");
      return false;
    }

    if (!timeDue) {
      setError("Response due time is required");
      return false;
    }

    const selectedMoment = combineDateAndTime();
    const minMoment = dayjs().add(24, "hours");

    if (selectedMoment.isBefore(minMoment)) {
      setError("Due date and time must be at least 24 hours from now");
      return false;
    }

    setError("");
    return true;
  };

  const handleConfirm = async () => {
    if (!validateDateTime()) {
      return;
    }

    const dateWithTimezone = combineDateAndTime().toISOString();
    await onConfirm(dateWithTimezone);
  };

  const handleDateChange = (value: Date) => {
    setDateDue(value);
    if (error) {
      setError("");
    }
  };

  const handleTimeChange = (value: string) => {
    setTimeDue(value);
    if (error) {
      setError("");
    }
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    if (error) {
      setError("");
    }
  };

  const FooterComponent = () => {
    return (
      <div className="flex w-full justify-between gap-2">
        <Button onClick={onClose} btntype="base" className="!w-full" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          className="!w-full"
          onClick={handleConfirm}
          disabled={isLoading || !!error || !isConfirmed}
        >
          {isLoading ? <Spinner size="sm" /> : "Confirm & Send"}
        </Button>
      </div>
    );
  };

  const headerDescription = isMultiSupplier
    ? `Send ${clarifications.length} clarification${clarifications.length !== 1 ? "s" : ""} to ${supplierCount} supplier${supplierCount !== 1 ? "s" : ""}`
    : `Send ${clarifications.length} clarification${clarifications.length !== 1 ? "s" : ""} to ${derivedSupplierName}`;

  // --- Column: the form/controls (date/time/warning/confirm/info) ---
  const formColumn = (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="mb-1 text-sm font-semibold text-amber-800">
          IMPORTANT: This is a FORMAL clarification request
        </p>
        <p className="text-sm text-amber-800">
          Once sent, clarification text cannot be edited. The supplier will receive
          an official notification. After the supplier responds, you may use the
          informal chat thread for follow-up discussion, but the formal response
          on the register will be final.
        </p>
      </div>

      <div>
        <DateInputWithLabel
          hasError={!!error}
          label="Response Due Date / Time"
          name="date_due"
          required
          value={dateDue}
          onChange={handleDateChange}
          minDate={dayjs().add(24, "hours").toDate()}
        />
      </div>

      <div>
        <TimePickerWithLabel
          hasError={!!error}
          name="time_due"
          nameTimezone="timezone_due"
          value={timeDue}
          timezone={timezone}
          onTimeChange={handleTimeChange}
          onTimeZoneChange={handleTimezoneChange}
          required
          restrictTimezones="Australia"
        />
      </div>
      <FormikFieldError text={error} />

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
        <p className="text-sm text-blue-800">
          The supplier{supplierCount > 1 ? "s" : ""} will receive these clarifications and will have until{" "}
          <span className="font-semibold">
            {dateDue && timeDue
              ? combineDateAndTime().format("MMMM D, YYYY [at] h:mm A z")
              : "the selected date"}
          </span>{" "}
          to respond.
        </p>
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3">
        <input
          type="checkbox"
          checked={isConfirmed}
          onChange={(e) => setIsConfirmed(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
        />
        <span className="text-sm text-gray-700">
          I understand this is a formal request that will be sent to the supplier and cannot be recalled
        </span>
      </label>
    </div>
  );

  // --- Column: the single-supplier inline clarification list ---
  const singleSupplierListBlock = (
    <div className="rounded-lg bg-gray-50 p-4">
      <div className="mb-2 text-sm font-medium text-gray-700">
        Sending {clarifications.length} clarification{clarifications.length !== 1 ? "s" : ""} to:
      </div>
      <div className="mb-3 text-base font-semibold text-gray-900">{derivedSupplierName}</div>
      <ClarificationSendList clarifications={clarifications} />
    </div>
  );

  // --- Body layout depends on mode + breakpoint ---
  let body: React.ReactNode;

  if (!isMultiSupplier) {
    body = (
      <div className="space-y-4" data-testid="send-modal-single-supplier">
        {singleSupplierListBlock}
        {formColumn}
      </div>
    );
  } else if (isDesktopMulti) {
    body = (
      <div
        className="flex flex-col gap-6 md:flex-row"
        data-testid="send-modal-multi-desktop"
      >
        <div className="w-full md:w-1/2">{formColumn}</div>
        <div className="w-full md:w-1/2">
          <SupplierAccordionPanel clarifications={clarifications} />
        </div>
      </div>
    );
  } else {
    body = (
      <div className="space-y-4" data-testid="send-modal-multi-mobile">
        <SupplierAccordionPanel clarifications={clarifications} />
        {formColumn}
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Final Clarification Request"
      description={headerDescription}
      iconType="mail"
      size={effectiveSize}
      customFooter={<FooterComponent />}
    >
      {body}
    </Modal>
  );
};
